// SupplierMinimalPrintPage.js

import React, { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../services/api';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useUser } from '../contexts/UserContext';
import { generateBusinessHeader, openPrintWindow } from '../utils/printUtils';

function SupplierMinimalPrintPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile } = useUser();
  const businessProfile = userProfile?.group?.business_profile || {};

  // Read the ordering the user had on the list. Default = alphabetical by name.
  const params = new URLSearchParams(location.search);
  const orderingParam = params.get('ordering') || 'SupplierName';

  // Fetch suppliers in the same order the list page uses, so the print
  // reflects exactly what the user was looking at.
  const {
    data: suppliers = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['suppliers-for-print', orderingParam],
    queryFn: async () => {
      const response = await API.get('/suppliers/', {
        params: orderingParam ? { ordering: orderingParam } : undefined,
      });
      return response.data;
    },
  });

  // Backend already orders by `orderingParam`. Special-case SupplierName so
  // Hebrew/Arabic collation still uses Intl.Collator (postgres ICU may differ
  // depending on locale, and the previous print used JS collator).
  const sortedSuppliers = useMemo(() => {
    const isDesc = orderingParam.startsWith('-');
    const field = isDesc ? orderingParam.slice(1) : orderingParam;
    if (field !== 'SupplierName') {
      return suppliers; // trust the backend ordering
    }
    const collator = new Intl.Collator(['he', 'ar', 'en'], {
      numeric: true,
      sensitivity: 'base',
    });
    return [...suppliers].sort((a, b) => {
      const cmp = collator.compare(
        (a.SupplierName || '').trim(),
        (b.SupplierName || '').trim()
      );
      return isDesc ? -cmp : cmp;
    });
  }, [suppliers, orderingParam]);

  // Guard against multiple invocations from re-renders / StrictMode.
  const hasPrintedRef = useRef(false);

  // On load, open new window & print the suppliers
  useEffect(() => {
    if (!isLoading && !isError && !hasPrintedRef.current) {
      const timer = setTimeout(() => {
        if (hasPrintedRef.current) return;
        hasPrintedRef.current = true;
        printSuppliersInNewWindow(sortedSuppliers);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [isLoading, isError, sortedSuppliers]);

  // ==================== PRINT LOGIC ====================
  const printSuppliersInNewWindow = (suppliersData) => {
    const htmlContent = generatePrintHTML(suppliersData);
    openPrintWindow(htmlContent);
    // Explicit return to /suppliers — `navigate(-1)` overshoots when history
    // contains more than just the suppliers→print pair.
    navigate('/suppliers', { replace: true });
  };

  // Helper: format currency
  const formatCurrency = (num) => {
    const val = parseFloat(num) || 0;
    return val.toLocaleString('he-IL', {
      style: 'currency',
      currency: 'ILS',
    });
  };

  // ============== Build raw HTML ==============
  const generatePrintHTML = (suppliersData) => {
    const style = `
      <style>
        @media print {
          @page {
            margin: 10mm;
          }
          thead {
            display: table-header-group;
          }
          tr {
            page-break-inside: avoid;
          }
        }
        table {
          border-collapse: collapse;
          width: 100%;
          font-family: Arial, sans-serif;
          direction: rtl;
        }
        th, td {
          border: 1px solid #ccc;
          padding: 4px;
          text-align: center;
          font-size: 16px;
        }
        th {
          background-color: #f0f0f0;
          font-weight: bold;
        }
        .balance-positive {
          color: #c62828;
          font-weight: bold;
        }
        .balance-negative {
          color: #2e7d32;
          font-weight: bold;
        }
      </style>
    `;

    const tableHeader = `
      <thead>
        <tr>
          <th>מס'</th>
          <th>שם הספק</th>
          <th>ח.פ./ע.מ.</th>
          <th>יתרה</th>
        </tr>
      </thead>
    `;

    // Build the rows
    const rowsHTML = suppliersData
      .map((supplier, index) => {
        const balance = parseFloat(supplier.Balance || 0);
        const balanceClass = balance > 0 ? 'balance-positive' : balance < 0 ? 'balance-negative' : '';
        const balanceFormatted = formatCurrency(balance);

        return `
          <tr>
            <td>${index + 1}</td>
            <td>${supplier.SupplierName || ''}</td>
            <td>${supplier.SupplierBusinessID || ''}</td>
            <td class="${balanceClass}">${balanceFormatted}</td>
          </tr>
        `;
      })
      .join('');

    return `
      <!DOCTYPE html>
      <html dir="rtl" lang="he">
      <head>
        <meta charset="UTF-8" />
        ${style}
        <title>ספקים</title>
      </head>
      <body>
        ${generateBusinessHeader(businessProfile)}
        <table>
          ${tableHeader}
          <tbody>
            ${rowsHTML}
          </tbody>
        </table>
      </body>
      </html>
    `;
  };

  // ============== RENDER STATES ==============
  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          טוען נתונים להדפסה...
        </Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 3, color: 'red' }}>
        <Typography variant="h6">שגיאה בטעינת הספקים להדפסה</Typography>
        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/suppliers', { replace: true })}>
          חזרה לרשימת הספקים
        </Button>
      </Box>
    );
  }

  // If loaded successfully, we show a minimal screen
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6">פתיחת חלון חדש להדפסה...</Typography>
      <Button variant="outlined" onClick={() => navigate('/suppliers', { replace: true })} sx={{ mt: 2 }}>
        חזרה לרשימת הספקים
      </Button>
    </Box>
  );
}

export default SupplierMinimalPrintPage;
